import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChevronLeft, ChevronRight, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { STATUS, TRAINING_TYPES, addDays, apiCall, eventSlot, fmtDate, parseISO, startOfWeek, toISO, todayBerlin } from "./calendar/calendarUtils";
import { TimeGrid, MonthView, ListView } from "./calendar/CalendarViews";
import { CalendarKpis, StatusLegend } from "./calendar/CalendarKpis";
import { ConflictsPanel } from "./calendar/ConflictsPanel";
import { AppointmentDialog } from "./calendar/AppointmentDialog";
import { DateField } from "./calendar/DateField";
import { BlockList, TimeBlockDialog } from "./calendar/TimeBlocks";
import { Lock } from "lucide-react";

const VIEWS = [["day", "Tag"], ["week", "Woche"], ["month", "Monat"], ["list", "Anstehend"], ["requests", "Offene Anfragen"]];
const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];

function rangeFor(view, anchor, listRange) {
  if (view === "day") return [anchor, anchor];
  if (view === "week") { const s = startOfWeek(anchor); return [s, addDays(s, 6)]; }
  if (view === "month") {
    const d = parseISO(anchor);
    const s = startOfWeek(toISO(new Date(d.getFullYear(), d.getMonth(), 1)));
    return [s, addDays(s, 41)];
  }
  if (view === "list") return [listRange.from, listRange.to];
  return [null, null];
}

const Sel = ({ value, onChange, children, testId }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className="h-9 px-2 bg-[#1A1A1A] border border-white/10 text-white text-sm" data-testid={testId}>{children}</select>
);

export default function KalenderTab({ adminToken: token, compact = false }) {
  const [view, setView] = useState(() => (window.innerWidth < 768 ? "list" : "week"));
  const [anchor, setAnchor] = useState(todayBerlin());
  const [listRange, setListRange] = useState({ from: todayBerlin(), to: addDays(todayBerlin(), 60) });
  const [data, setData] = useState({ events: [], periods: [], blocked_dates: [] });
  const [stats, setStats] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [filters, setFilters] = useState({ q: "", training: "all", status: "all" });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [blockDlg, setBlockDlg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [start, end] = rangeFor(view, anchor, listRange);
    const qs = new URLSearchParams();
    if (start) qs.set("start", start);
    if (end) qs.set("end", end);
    if (view === "requests") qs.set("status", "pending,alternative_proposed");
    try {
      const [ev, st, cf] = await Promise.all([
        apiCall(token, `/calendar/admin/events?${qs}`), apiCall(token, "/calendar/admin/stats"), apiCall(token, "/calendar/admin/conflicts"),
      ]);
      setData(ev); setStats(st); setConflicts(cf.conflicts);
      setSelected((cur) => (cur ? ev.events.find((x) => x.appointment_id === cur.appointment_id) || cur : cur));
    } catch (err) { toast.error(err.message); }
    setLoading(false);
  }, [token, view, anchor, listRange]);

  useEffect(() => { load(); }, [load]);

  const refreshSelected = async () => {
    if (selected) setSelected(await apiCall(token, `/calendar/admin/appointments/${selected.appointment_id}`).catch(() => null));
    load();
  };
  const openById = async (id) => setSelected(await apiCall(token, `/calendar/admin/appointments/${id}`));

  const events = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    let list = data.events.filter((e) =>
      (filters.training === "all" || e.training_type === filters.training) &&
      (filters.status === "all" || e.display_status === filters.status) &&
      (!q || [e.customer?.name, e.customer?.email, e.customer?.phone, e.player_name].join(" ").toLowerCase().includes(q)));
    if (view === "list") list = list.filter((e) => ["pending", "confirmed", "alternative"].includes(e.display_status) || filters.status !== "all");
    return list.sort((a, b) => `${eventSlot(a).date}${eventSlot(a).time}`.localeCompare(`${eventSlot(b).date}${eventSlot(b).time}`));
  }, [data.events, filters, view]);

  const periodsFor = (d) => [
    ...data.blocked_dates.filter((b) => b.date === d).map((b) => ({ id: b.id, name: b.reason || "Gesperrt" })),
    ...data.periods.filter((p) => p.date_from <= d && d <= p.date_to && (!p.weekdays?.length || p.weekdays.includes((parseISO(d).getDay() + 6) % 7))),
  ];

  const step = (dir) => {
    if (view === "day") setAnchor(addDays(anchor, dir));
    else if (view === "week") setAnchor(addDays(anchor, 7 * dir));
    else if (view === "month") { const d = parseISO(anchor); setAnchor(toISO(new Date(d.getFullYear(), d.getMonth() + dir, 1))); }
  };
  const [rs, re] = rangeFor(view, anchor, listRange);
  const title = view === "day" ? fmtDate(anchor) : view === "week" ? `${fmtDate(rs)} – ${fmtDate(re)}` : view === "month" ? `${MONTHS[parseISO(anchor).getMonth()]} ${parseISO(anchor).getFullYear()}` : view === "list" ? "Anstehende Termine" : "Offene Terminanfragen";

  return (
    <div data-testid="kalender-tab">
      {!compact && <CalendarKpis stats={stats} onView={(v) => { setView(v); setAnchor(todayBerlin()); }} />}
      <ConflictsPanel conflicts={conflicts} onOpen={openById} />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex flex-wrap border border-white/10 max-w-full" data-testid="view-switch">
          {VIEWS.map(([id, label]) => (
            <button key={id} type="button" onClick={() => setView(id)} className={`px-3 py-2 text-sm whitespace-nowrap transition-colors ${view === id ? "bg-[#c1ff72] text-black font-semibold" : "text-white hover:bg-white/5"}`} data-testid={`view-${id}`}>
              {label}{id === "requests" && stats?.open_requests ? ` (${stats.open_requests})` : ""}
            </button>
          ))}
        </div>
        {["day", "week", "month"].includes(view) && (
          <div className="flex items-center gap-1">
            <Button size="icon" variant="ghost" onClick={() => step(-1)} data-testid="cal-prev"><ChevronLeft className="w-5 h-5" /></Button>
            <Button size="sm" variant="outline" onClick={() => setAnchor(todayBerlin())} className="border-white/20 text-white rounded-none" data-testid="cal-today">Heute</Button>
            <Button size="icon" variant="ghost" onClick={() => step(1)} data-testid="cal-next"><ChevronRight className="w-5 h-5" /></Button>
          </div>
        )}
        <h3 className="font-['Bebas_Neue'] text-2xl text-white tracking-wide ml-1" data-testid="cal-title">{title}</h3>
        <Button size="sm" onClick={() => setBlockDlg({ block: null })} className="ml-auto rounded-none bg-[#7f1d1d] hover:bg-[#991b1b] text-white font-bold tracking-wide" data-testid="cal-block-time"><Lock className="w-4 h-4 mr-1" />ZEIT SPERREN</Button>
        <Button size="sm" variant="ghost" onClick={load} className="text-[#A1A1AA]" data-testid="cal-refresh"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="cal-filters">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2 top-2.5 text-[#71717A]" />
          <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Kunde, Spieler, Telefon, E-Mail" className="h-9 pl-8 w-64 bg-[#1A1A1A] border-white/10 text-white" data-testid="filter-search" />
        </div>
        <Sel value={filters.training} onChange={(v) => setFilters({ ...filters, training: v })} testId="filter-training">
          <option value="all">Alle Leistungen</option>
          {TRAINING_TYPES.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </Sel>
        <Sel value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })} testId="filter-status">
          <option value="all">Alle Status</option>
          {Object.entries(STATUS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
        </Sel>
        {view === "list" && (
          <div className="flex items-center gap-1 text-sm text-[#A1A1AA]">
            <DateField value={listRange.from} onChange={(v) => setListRange({ ...listRange, from: v })} testId="filter-from" className="!h-9 w-36" />
            bis
            <DateField value={listRange.to} onChange={(v) => setListRange({ ...listRange, to: v })} testId="filter-to" className="!h-9 w-36" />
          </div>
        )}
      </div>
      <div className="mb-4"><StatusLegend /></div>

      {view === "day" && <TimeGrid days={[anchor]} events={events} onOpen={setSelected} periodsFor={periodsFor} blocks={data.time_blocks || []} onBlock={(b) => setBlockDlg({ block: b })} holidays={data.holidays || {}} />}
      {view === "week" && <TimeGrid days={Array.from({ length: 7 }, (_, i) => addDays(rs, i))} events={events} onOpen={setSelected} periodsFor={periodsFor} blocks={data.time_blocks || []} onBlock={(b) => setBlockDlg({ block: b })} holidays={data.holidays || {}} />}
      {view === "month" && <MonthView anchor={anchor} events={events} onOpen={setSelected} onPickDay={(d) => { setAnchor(d); setView("day"); }} periodsFor={periodsFor} holidays={data.holidays || {}} />}
      {view === "list" && <ListView events={events} onOpen={setSelected} empty="Keine anstehenden Termine im gewählten Zeitraum" testId="upcoming-list" />}
      {view === "requests" && <ListView events={events} onOpen={setSelected} empty="Keine offenen Anfragen – alles erledigt." testId="requests-list" />}

      {["month", "list"].includes(view) && <BlockList blocks={data.time_blocks || []} onOpen={(b) => setBlockDlg({ block: b })} />}
      <TimeBlockDialog token={token} open={!!blockDlg} block={blockDlg?.block} defaultDate={view === "day" ? anchor : todayBerlin()} onClose={() => setBlockDlg(null)} onSaved={() => { setBlockDlg(null); load(); }} />
      <AppointmentDialog token={token} event={selected} onClose={() => setSelected(null)} onChanged={refreshSelected} />
    </div>
  );
}
